import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_DASHBOARD_MAP, AVAILABLE_ROLES_FOR_LOGIN, AVAILABLE_ROLES_FOR_REGISTER } from '../constants/roles';
import { Eye, EyeOff, Mail, Lock, User, CheckCircle, Info } from 'lucide-react';
import axios from 'axios';
import Hyperspeed from './Hyperspeed';
import { BACKEND_URL } from '../lib/backendUrl';

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showStudentOTP, setShowStudentOTP] = useState(false);
  const [otpActuallySent, setOtpActuallySent] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [role, setRole] = useState('student');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { login, register, setAuthSession, user, token } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to their dashboard AFTER login/register
  React.useEffect(() => {
    if (user && token && isAuthenticating) {
      setIsAuthenticating(false);
      const normalizedRole = user.role.toUpperCase().replace('-', '_');
      const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole as keyof typeof ROLE_DASHBOARD_MAP] || '/dashboard';
      console.log('Redirecting to dashboard:', dashboardPath);
      navigate(dashboardPath, { replace: true });
    }
  }, [user, token, isAuthenticating, navigate]);

  // Memoized input change handlers to prevent unnecessary re-renders
  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  }, []);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  }, []);

  const handleConfirmPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
  }, []);

  const handleOtpChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
  }, []);

  const handleRoleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setRole(e.target.value);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  // Handle student OTP registration - Send OTP
  const handleStudentOTPSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || !email || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setIsAuthenticating(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/register/student/send-otp`,
        { email },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.success) {
        setOtpActuallySent(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Handle student OTP verification
  const handleStudentOTPVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setIsAuthenticating(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/register/student/verify-otp`,
        { email, otp, username, password },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.token) {
        if (response.data.user) {
          setAuthSession(response.data.token, response.data.user);
        } else {
          await login(username, password, 'student');
        }
        setIsAuthenticating(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'OTP verification failed. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (isLogin) {
        await login(username, password, role);
      } else {
        // Show student OTP registration form
        if (role === 'student') {
          setShowStudentOTP(true);          setOtpActuallySent(false);          return;
        }
        // Club admins need to be invited by super admin
        if (role === 'club-admin') {
          setError('Club Admins must be invited by a Super Admin. Please contact administration.');
          return;
        }
        // Super admin registration
        await register(username, email, password, role);
      }
      // Set flag to trigger redirect in useEffect after state updates
      setIsAuthenticating(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.response?.data?.error || 'Authentication failed');
    }
  };

  // Memoize Hyperspeed options to prevent recreation on every render
  const hyperspeedOptions = useMemo(() => ({
    distortion: 'turbulentDistortion',
    speedUp: 2,
    colors: {
      roadColor: 0x080808,
      islandColor: 0x0a0a0a,
      background: 0x000000,
      shoulderLines: 0x00ffff,
      brokenLines: 0x00ffff,
      leftCars: [0xd856bf, 0x6750a2, 0xc247ac],
      rightCars: [0x03b3c3, 0x0e5ea5, 0x324555],
      sticks: 0x03b3c3
    }
  }), []);

  return (
    <div className="auth-page">
      <Hyperspeed effectOptions={hyperspeedOptions} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="auth-container"
      >
        <button
          type="button"
          className="auth-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <h1 className="neon-title">NEXUS</h1>
        <p className="auth-subtitle">EVENT MANAGEMENT SYSTEM</p>

        {showStudentOTP ? (
          // Student OTP Registration Form
          <form onSubmit={handleStudentOTPVerify} className="auth-form">
            {!otpActuallySent && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div style={{ position: 'relative', marginBottom: '20px' }}>
                  <User size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#00ffff' }} />
                  <input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={handleUsernameChange}
                    className="cyber-input"
                    style={{ paddingLeft: '45px' }}
                    required
                    disabled={isAuthenticating}
                  />
                </div>

                <div style={{ position: 'relative', marginBottom: '20px' }}>
                  <Mail size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#00ffff' }} />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={handleEmailChange}
                    className="cyber-input"
                    style={{ paddingLeft: '45px' }}
                    required
                    disabled={isAuthenticating}
                  />
                </div>

                <div style={{ position: 'relative', marginBottom: '20px' }}>
                  <Lock size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#00ffff' }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password (min 8 characters)"
                    value={password}
                    onChange={handlePasswordChange}
                    className="cyber-input"
                    style={{ paddingLeft: '45px', paddingRight: '45px' }}
                    required
                    disabled={isAuthenticating}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    disabled={isAuthenticating}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: isAuthenticating ? 'not-allowed' : 'pointer',
                      color: '#00ffff',
                      opacity: isAuthenticating ? 0.5 : 1
                    }}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                <div style={{ position: 'relative', marginBottom: '20px' }}>
                  <Lock size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#00ffff' }} />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    className="cyber-input"
                    style={{ paddingLeft: '45px', paddingRight: '45px' }}
                    required
                    disabled={isAuthenticating}
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    disabled={isAuthenticating}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: isAuthenticating ? 'not-allowed' : 'pointer',
                      color: '#00ffff',
                      opacity: isAuthenticating ? 0.5 : 1
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      backgroundColor: 'rgba(220, 53, 69, 0.2)',
                      border: '1px solid #dc3545',
                      color: '#ff6b6b',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      fontSize: '14px'
                    }}
                  >
                    {error}
                  </motion.div>
                )}

                <button
                  type="button"
                  onClick={handleStudentOTPSend}
                  disabled={isAuthenticating}
                  className="cyber-button"
                  style={{
                    cursor: isAuthenticating ? 'not-allowed' : 'pointer',
                    opacity: isAuthenticating ? 0.5 : 1,
                    width: '100%'
                  }}
                >
                  {isAuthenticating ? 'Sending verification code...' : 'Verify Email & Proceed'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowStudentOTP(false);
                    setOtpActuallySent(false);
                    setError('');
                    setUsername('');
                    setEmail('');
                    setPassword('');
                    setConfirmPassword('');
                  }}
                  disabled={isAuthenticating}
                  style={{
                    background: 'none',
                    border: '1px solid #00ffff',
                    color: '#00ffff',
                    cursor: isAuthenticating ? 'not-allowed' : 'pointer',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '100%',
                    marginTop: '12px',
                    opacity: isAuthenticating ? 0.5 : 1
                  }}
                >
                  ← Back to Login
                </button>
              </motion.div>
            )}

            {otpActuallySent && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div style={{
                  backgroundColor: 'rgba(0, 255, 255, 0.1)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  marginTop: '20px',
                  textAlign: 'center'
                }}>
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <CheckCircle size={32} color="#00ffff" style={{ margin: '0 auto 12px' }} />
                  </motion.div>
                  <p style={{ color: '#00ffff', marginBottom: '8px', fontWeight: '600' }}>Verification code sent!</p>
                  <p style={{ color: '#aaa', fontSize: '14px' }}>Check your email {email}</p>
                </div>

                <div style={{ position: 'relative', marginBottom: '24px' }}>
                  <input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={handleOtpChange}
                    className="cyber-input"
                    maxLength={6}
                    style={{
                      fontSize: '28px',
                      letterSpacing: '12px',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      padding: '16px 12px'
                    }}
                    required
                    disabled={isAuthenticating}
                    autoFocus
                  />
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      backgroundColor: 'rgba(220, 53, 69, 0.2)',
                      border: '1px solid #dc3545',
                      color: '#ff6b6b',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      fontSize: '14px'
                    }}
                  >
                    {error}
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={isAuthenticating || otp.toString().length !== 6}
                  className="cyber-button"
                  style={{
                    cursor: (isAuthenticating || otp.toString().length !== 6) ? 'not-allowed' : 'pointer',
                    opacity: (isAuthenticating || otp.toString().length !== 6) ? 0.5 : 1,
                    width: '100%',
                    marginBottom: '16px'
                  }}
                >
                  {isAuthenticating ? 'Verifying & Creating Account...' : 'Complete Registration'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                  setShowStudentOTP(false);
                    setOtpActuallySent(false);
                    setError('');
                    setOtp('');
                  }}
                  disabled={isAuthenticating}
                  style={{
                    background: 'none',
                    border: '1px solid #00ffff',
                    color: '#00ffff',
                    cursor: isAuthenticating ? 'not-allowed' : 'pointer',
                    padding: '12px 24px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '100%',
                    opacity: isAuthenticating ? 0.5 : 1
                  }}
                >
                  ← Use different email
                </button>
              </motion.div>
            )}
          </form>
        ) : (
          // Regular Login/Register Form
          <form onSubmit={handleSubmit} className="auth-form">
          <input
            type="text"
            placeholder={isLogin ? "Username" : "Username"}
            value={username}
            onChange={handleUsernameChange}
            className="cyber-input"
            required
          />
          {!isLogin && (
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={handleEmailChange}
              className="cyber-input"
              required
            />
          )}
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={handlePasswordChange}
              className="cyber-input"
              required
              style={{ 
                width: '100%',
                paddingRight: '45px',
                boxSizing: 'border-box'
              }}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00ffff',
                padding: '4px',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#00ffff')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#00ffff')}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {!isLogin && (
            <div style={{
              fontSize: '12px',
              color: '#00ffff',
              opacity: 0.8,
              marginTop: '8px',
              padding: '8px',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '4px',
              fontFamily: 'Rajdhani, monospace',
            }}>
              Password must contain: 8+ characters, uppercase, lowercase, number, and special character (@$!%*?&)
            </div>
          )}
          {isLogin && (
            <select value={role} onChange={handleRoleChange} className="cyber-select">
              {AVAILABLE_ROLES_FOR_LOGIN.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          )}
          {!isLogin && (
            <p style={{
              fontSize: '12px',
              color: '#00ffff',
              opacity: 0.8,
              marginTop: '8px',
              padding: '8px',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '4px',
              fontFamily: 'Rajdhani, monospace',
            }}>
              <Info size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Students register with OTP verification for security
            </p>
          )}
          {error && <p className="error-text">{error}</p>}
          <button type="submit" className="cyber-button">
            {isLogin ? 'LOGIN' : 'REGISTER'}
          </button>
          </form>
        )}

        <p className="auth-switch">
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <span onClick={() => setIsLogin(!isLogin)} className="neon-link">
            {isLogin ? 'Register' : 'Login'}
          </span>
        </p>
      </motion.div>
    </div>
  );
};

export default AuthPage;
